import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, BookOpenIcon } from "@heroicons/react/24/outline";
import RoleOnly from "../../../routes/roleOnly";
import { HseKsGuideSection } from "../../../components/dashboard/companyGuide/HseKsGuideSection";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 border-b border-slate-100 py-8 last:border-b-0">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

export const UserGuideHseKsGettingStarted = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.title = `${t("user_guide.hse.title")} | Promang`;
  }, [t]);

  useEffect(() => {
    const raw = location.hash?.replace(/^#/, "");
    if (!raw) return;
    const el = document.getElementById(raw);
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/dashboard/guide"
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t("user_guide.hse.back_to_hub")}
        </Link>

        <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-8 shadow-sm sm:px-8">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            <BookOpenIcon className="h-4 w-4" />
            {t("user_guide.hse.badge")}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">{t("user_guide.hse.title")}</h1>
          <p className="mt-3 text-sm text-slate-600">{t("user_guide.hse.purpose")}</p>

          <div className="mt-6">
            <RoleOnly role="admin">
              <HseKsGuideSection />
            </RoleOnly>
          </div>

          <Section id="roles" title={t("user_guide.hse.section.roles.title")}>
            <p>{t("user_guide.hse.section.roles.p1")}</p>
            <p>{t("user_guide.hse.section.roles.p2")}</p>
          </Section>

          <Section id="goals" title={t("user_guide.hse.section.goals.title")}>
            <p>{t("user_guide.hse.section.goals.p1")}</p>
            <p>{t("user_guide.hse.section.goals.p2")}</p>
          </Section>

          <Section id="risk" title={t("user_guide.hse.section.risk.title")}>
            <p>{t("user_guide.hse.section.risk.p1")}</p>
            <p>{t("hse.guide.risk.lead")}</p>
          </Section>
          <Section id="sja" title={t("hse.guide.sja.title")}>
            <p>{t("hse.guide.sja.lead")}</p>
          </Section>
          <Section id="chemical" title={t("hse.guide.chemical.title")}>
            <p>{t("hse.guide.chemical.lead")}</p>
          </Section>
          <Section id="protective" title={t("hse.guide.protective.title")}>
            <p>{t("hse.guide.protective.lead")}</p>
          </Section>
          <Section id="evaluation" title={t("hse.guide.evaluation.title")}>
            <p>{t("hse.guide.evaluation.lead")}</p>
          </Section>
          <Section id="tiltak" title={t("hse.guide.tiltak.title")}>
            <p>{t("hse.guide.tiltak.lead")}</p>
          </Section>

          <Section id="related" title={t("user_guide.hse.section.related.title")}>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <Link className="font-medium text-slate-900 underline decoration-slate-300" to="/dashboard/hse-ks/settings">
                  {t("user_guide.hse.section.related.settings")}
                </Link>
              </li>
              <li>
                <Link className="font-medium text-slate-900 underline decoration-slate-300" to="/dashboard/hse-ks/goals">
                  {t("user_guide.hse.section.related.goals")}
                </Link>
              </li>
              <li>
                <Link className="font-medium text-slate-900 underline decoration-slate-300" to="/dashboard/hse-ks/risk">
                  {t("user_guide.hse.section.related.risk")}
                </Link>
              </li>
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
};
